import { useMutation } from "@tanstack/react-query";
import { backendService } from "@/services/backend.service";
import { trackEvent } from "@/utils/analytics";
import type { WelcomeResetRequest } from "@/utils/models";

export class WelcomeResetError extends Error {
	constructor(
		message: string,
		public isReset: boolean,
	) {
		super(message);
	}
}

async function handleResetRequest(request: WelcomeResetRequest) {
	try {
		const response = await backendService.sendNurseReset({
			emailOrPhoneNumber: request.emailOrPhoneNumber ?? "",
			sendFromMultipleCommunications: true,
		});
		trackEvent("reset_email_sms_sent", {
			nurse_id: request.nurseId,
			department_id: request.departmentId,
			is_new_user: request.shouldCreateUser,
		});
		return response.status === 200;
	} catch (error) {
		trackEvent("reset_password_error", {
			nurse_id: request.nurseId,
			department_id: request.departmentId,
			is_new_user: request.shouldCreateUser,
			error: error instanceof Error ? error.message : "Unknown error",
		});
		throw new WelcomeResetError(error instanceof Error ? error.message : "Unknown error", request.isReset);
	}
}

async function handleWelcomeRequest(request: WelcomeResetRequest) {
	try {
		const response = await backendService.sendNurseWelcome({
			nurseId: request.nurseId,
			departmentId: request.departmentId,
		});
		trackEvent("welcome_email_sent", {
			nurse_id: request.nurseId,
			department_id: request.departmentId,
			is_new_user: request.shouldCreateUser,
		});
		return response.status === 200;
	} catch (error) {
		console.error(`Could not create a user for nurse #${request.nurseId}`, error);
		trackEvent("welcome_email_error", {
			nurse_id: request.nurseId,
			department_id: request.departmentId,
			is_new_user: request.shouldCreateUser,
			error: error instanceof Error ? error.message : "Unknown error",
		});
		throw new WelcomeResetError(error instanceof Error ? error.message : "Unknown error", request.isReset);
	}
}

export function useWelcomeReset() {
	const {
		mutateAsync: sendWelcomeReset,
		isPending: isLoading,
		error,
		isError,
	} = useMutation({
		mutationFn: async (request: WelcomeResetRequest): Promise<boolean> => {
			return request.isReset ? await handleResetRequest(request) : await handleWelcomeRequest(request);
		},
	});

	return {
		sendWelcomeReset,
		isError,
		isLoading,
		error,
	};
}
